import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  useAdminPrivacyRequests,
  useProcessDeletions,
} from '../../hooks/usePrivacy';
import type { DeletionRequestAdmin } from '../../api/client';

function statusColor(status: string): 'warning' | 'info' | 'success' | 'error' {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'PROCESSING':
      return 'info';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    default:
      return 'info';
  }
}

function requestTypeLabel(type: string): string {
  switch (type) {
    case 'guest_deletion':
      return 'Data Deletion';
    case 'host_deletion':
      return 'Host Deletion';
    case 'data_export':
      return 'Data Export';
    default:
      return type;
  }
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

export default function PrivacyAdminPage() {
  const [page, setPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState<DeletionRequestAdmin | null>(null);
  const [processResult, setProcessResult] = useState<string | null>(null);

  const { data, isLoading, error } = useAdminPrivacyRequests(page, 20);
  const processMutation = useProcessDeletions();

  const handleProcessAll = async () => {
    setProcessResult(null);
    try {
      const result = await processMutation.mutateAsync();
      setProcessResult(
        `Processed ${result.processed} request${result.processed !== 1 ? 's' : ''}.`,
      );
    } catch {
      setProcessResult('Failed to process deletion requests.');
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load privacy requests. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Privacy Requests
        </Typography>
        <Button
          variant="contained"
          startIcon={
            processMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <PlayArrowIcon />
            )
          }
          onClick={handleProcessAll}
          disabled={processMutation.isPending}
          sx={{ minHeight: 44 }}
        >
          {processMutation.isPending ? 'Processing...' : 'Process Pending'}
        </Button>
      </Box>

      {processResult && (
        <Alert
          severity={processResult.includes('Failed') ? 'error' : 'success'}
          onClose={() => setProcessResult(null)}
          sx={{ mb: 2 }}
        >
          {processResult}
        </Alert>
      )}

      {/* Loading skeletons */}
      {isLoading && (
        <Stack spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={100}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>
      )}

      {/* Request cards */}
      {!isLoading && data && (
        <>
          {data.requests.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  No privacy requests yet.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={2}>
              {data.requests.map((req) => (
                <Card key={req.id}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: 14, sm: 16 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {req.email}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Chip
                            label={requestTypeLabel(req.requestType)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={req.status}
                            size="small"
                            color={statusColor(req.status)}
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          Submitted {formatDate(req.createdAt)}
                          {req.processedAt &&
                            ` | Processed ${formatDate(req.processedAt)}`}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setSelectedRequest(req)}
                        sx={{ minHeight: 44, flexShrink: 0 }}
                      >
                        Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {/* Pagination */}
          {data.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={data.totalPages}
                page={page}
                onChange={(_e, val) => setPage(val)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <RequestDetailDialog
        request={selectedRequest}
        onClose={() => setSelectedRequest(null)}
      />
    </Box>
  );
}

// ─── Detail Dialog ──────────────────────────────────────────────────────────

function RequestDetailDialog({
  request,
  onClose,
}: {
  request: DeletionRequestAdmin | null;
  onClose: () => void;
}) {
  if (!request) return null;

  return (
    <Dialog open={!!request} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Request Details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DetailRow label="ID" value={request.id} />
          <DetailRow label="Email" value={request.email} />
          <DetailRow label="Type" value={requestTypeLabel(request.requestType)} />
          <DetailRow
            label="Status"
            value={
              <Chip
                label={request.status}
                size="small"
                color={statusColor(request.status)}
              />
            }
          />
          <DetailRow label="Submitted" value={formatDate(request.createdAt)} />
          {request.processedAt && (
            <DetailRow
              label="Processed"
              value={formatDate(request.processedAt)}
            />
          )}
          {request.requestType === 'data_export' &&
            request.status === 'COMPLETED' && (
              <DetailRow
                label="Export"
                value={
                  <Button
                    size="small"
                    variant="outlined"
                    href={`/api/privacy/export/${request.id}`}
                    target="_blank"
                  >
                    Download
                  </Button>
                }
              />
            )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.5,
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>
        {typeof value === 'string' ? (
          <Typography
            variant="body2"
            sx={{ wordBreak: 'break-all', maxWidth: 280 }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
}
